import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import './PSUReport.css';
import html2pdf from "html2pdf.js";

const PSUReport = () => {
    const { claimNumber } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [isPdfGenerated, setIsPdfGenerated] = useState(false);
    const reportRef = useRef(null);

    const [formData, setFormData] = useState({
        claim_number: '',  
        email_id: '',
        phone_number: '',
        created_at: '',
        NameofDriver: '',
        dateTimeOfSurvey: '',
        placeOfSurvey: '',
        driverLicenseNo: '',
        driverAge: '',
        driverExperience: '',
        causeAndNature: '',
        driverValidLicense: ''
    });

    useEffect(() => {
        const handlePopState = () => {
            navigate(`/review-page/${claimNumber}`, { replace: true });
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [navigate, claimNumber]);

    useEffect(() => {
        const fetchClaimDetails = async () => {
            try {
                const res = await axios.post('/process_data/claim-details', {
                    claimNumber
                });
                console.log('PSU Report - Claim Details:', res.data);
                setFormData(res.data);
            } catch (err) {
                console.error('Error fetching claim details in PSU Report:', err);
            }
        };

        if (claimNumber) {
            fetchClaimDetails();
        }
    }, [claimNumber]);

    useEffect(() => {
        if (claimNumber) {
            axios
                .post(`/server/get-claim-data/${claimNumber}`)
                .then(response => {
                    console.log('PSU Report Data:', response.data);
                    setData(response.data);
                })
                .catch(error => console.error('Axios error:', error));
        }
    }, [claimNumber]);

    // Improved PDF generation with proper timing
    useEffect(() => {
        if (formData && data && !isPdfGenerated) {
            const timer = setTimeout(() => {
                generatePDF();
            }, 1500);
            
            return () => clearTimeout(timer);
        }
    }, [formData, data, isPdfGenerated]);

    const generatePDF = () => {
        const element = reportRef.current;
        if (!element) {
            console.error("Report element not found");
            return;
        }

        // PDF options with better table handling
        const opt = {
            margin: [10, 10, 10, 10],
            filename: `PSU_Report_${formData.claim_number || claimNumber}.pdf`,
            image: { 
                type: "jpeg", 
                quality: 0.98 
            },
            html2canvas: { 
                scale: 2,
                useCORS: true,
                logging: true,
                width: 1500,
                height: element.scrollHeight,
                windowWidth: 1600,
                scrollX: 0,
                scrollY: 0,
                letterRendering: true,
                allowTaint: true
            },
            jsPDF: { 
                unit: "mm", 
                format: [450, 990], 
                orientation: "portrait",
                compress: true
            },
            pagebreak: { 
                mode: ['avoid-all', 'css', 'legacy'] 
            }
        };

        // Additional PDF configuration for better table handling
        const pdfOptions = {
            ...opt,
            html2canvas: {
                ...opt.html2canvas,
                onclone: function(clonedDoc) {
                    // Add page break controls to cloned document
                    const tables = clonedDoc.querySelectorAll('table');
                    tables.forEach((table, index) => {
                        table.style.pageBreakInside = 'auto';
                        table.style.breakInside = 'auto';
                        
                        // Add responsive table styles
                        table.style.width = '100%';
                        table.style.tableLayout = 'fixed';
                        
                        // Add page break before large tables if needed
                        if (table.offsetHeight > 400) {
                            table.style.pageBreakBefore = 'auto';
                        }
                    });
                    
                    // Add table cells optimization
                    const tableCells = clonedDoc.querySelectorAll('td, th');
                    tableCells.forEach(cell => {
                        cell.style.wordWrap = 'break-word';
                        cell.style.overflowWrap = 'break-word';
                        cell.style.padding = '4px 6px';
                    });
                    
                    // Ensure proper styling for PDF
                    const style = clonedDoc.createElement('style');
                    style.innerHTML = `
                        @media print {
                            table { 
                                page-break-inside: auto !important;
                                break-inside: auto !important;
                                width: 100% !important;
                            }
                            tr { 
                                page-break-inside: avoid !important;
                                break-inside: avoid !important;
                            }
                            thead { 
                                display: table-header-group !important;
                            }
                            tfoot { 
                                display: table-footer-group !important;
                            }
                            td, th {
                                word-wrap: break-word !important;
                                overflow-wrap: break-word !important;
                            }
                            .section {
                                page-break-inside: avoid;
                                break-inside: avoid;
                            }
                        }
                    `;
                    clonedDoc.head.appendChild(style);
                }
            }
        };

        html2pdf()
            .set(pdfOptions)
            .from(element)
            .save()
            .then(() => {
                setIsPdfGenerated(true);
                console.log("PDF generated successfully");
            })
            .catch(error => {
                console.error("PDF generation error:", error);
            });
    };

    if (!data) return <div>Loading...</div>;

    return (
        <div className="psu-report" ref={reportRef}>
            {/* Top Navigation Bar */}
            <div className="report-header">
                <img src="/custom-icons/surveyorIcon.png" alt="Surveyor AI Logo" className="logo" />
                
                <div className="report-title">
                    <h2 className="title-psu">PSU Report</h2>
                </div>

                <p className="printed-date">
                    Printed: {new Date().toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    })}
                </p>
            </div>

            <div className="signature-section">
                <div className="signature-content">
                    {/* Left Section - Name and Qualification */}
                    <div className="left-section">
                        <div className="signature-name">{data.insured}</div>
                        <div className="qualification"></div>
                        <div className="license-item">
                            <span className="label">License No.</span>
                            <span className="value">- {formData.driverLicenseNo || data.DrivingLicenseNo || 'Not Reported'}</span>
                        </div>
                        <div className="license-item">
                            <span className="label">Validity</span>
                            <span className="value">- {formData.ValidUptoNTV || data.ValidUptoNTV || '02-03-2025'}</span>
                        </div>
                        <div className="license-item">
                            <span className="label">IIISLA M No.</span>
                            <span className="value">- A/W/06987</span>
                        </div>
                    </div>
                    
                    {/* Middle Section - License Info */}
                    <div className="middle-section">
                        <div className="license-info"></div>
                    </div>
                    
                    {/* Right Section - Address and Contact */}
                    <div className="right-section">
                        <div className="address-section">
                            <div className="address-section-title">Address</div>
                            <div className="address-details">
                                N-12 Chetakpuri, Jnansi Road, Gwalior (M.P.)<br />
                            </div>
                        </div>
                        
                        <div className="contact-info">
                            <div className="contact-item">
                                <span className="label">Cell:</span>
                                <span className="value">{formData.phone_number}</span>
                            </div>
                            <div className="contact-item">
                                <span className="label">Email:</span>
                                <span className="value">{formData.email_id}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="top-note-section">
                <h4 className="top-section-heading">MOTOR FINAL SURVEY REPORT - (NIL DEPRECIATION)</h4>
                <div className="note-title-section"></div>
                <p>
                    This report is issued by me/us as a licensed Surveyor(s) without prejudice in respect of cause, nature, and extent of loss/damages and subject to the terms and conditions of the insurance policy.<br />
                    Ref :No . NIA/MOT/10051/09/2K23<br />
                    Date: {new Date().toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    })}
                </p>
            </div>

            {/* Property Information */}
            <div className="section">
                <h4 className="section-heading">INSURANCE PARTICULARS</h4>
                <div className="inline-box-row info-box">
                    <div className="info-row">
                        <div className="inline-box label">Policy / Cover Note No</div>
                        <div className="inline-box value">{data.policyNumber || '11300031222002260231'}</div>
                        <div className="inline-box label">IDV</div>
                        <div className="inline-box value">₹ {data.idv || '621,600.00'}</div>
                        <div className="inline-box label">Period of Insurance</div>
                        <div className="inline-box value">{data.periodOfInsurance || '04/11/2022 to 03/11/2023'}</div>
                    </div>
                    <div className="info-row">
                        <div className="inline-box label">Insurers</div>
                        <div className="inline-box value">{data.insurers || 'The New India Assurance Co. Ltd. (Hyundai Tie-Up)'}</div>
                        <div className="inline-box label">Insured</div>
                        <div className="inline-box value">{data.insured || 'Mr. Ravi Shankar Bhardwaj'}</div>
                        <div className="inline-box label">Insured Address</div>
                        <div className="inline-box value">{data.insuredAddress || 'BM 84 B SEC DD Nagar, Gwalior'}</div>
                    </div>
                    <div className="info-row last-row">
                        <div className="inline-box label">H.P.A</div>
                        <div className="inline-box value">{data.hpa || 'M&M Financial Services Ltd., Gwalior'}</div>
                        <div className="inline-box label">Appointed By</div>
                        <div className="inline-box value">{data.appointedBy || 'D.O.-1, Gwalior'}</div>
                        <div className="inline-box label child-A"></div>
                        <div className="inline-box value child-B"></div>
                    </div>
                </div>
            </div>

            <div className="section">
                <h4 className="section-heading">VEHICLE PARTICULARS</h4>
                <div className="inline-box-row info-box">
                    <div className="info-row">
                        <div className="inline-box label">Registration No</div>
                        <div className="inline-box value">{data.VehicleRegistrationNumber || '--'}</div>
                        <div className="inline-box label">Make & Model</div>
                        <div className="inline-box value">{data.Make && data.Variant ? `${data.Make} ${data.Variant}` : 'Hyundai Creta'}</div>
                        <div className="inline-box label">Date of Registration</div>
                        <div className="inline-box value">{data.DateofRegistration || '--'}</div>
                    </div>
                    <div className="info-row">
                        <div className="inline-box label">Chassis No</div>
                        <div className="inline-box value">{data.ChassisNumber || '--'}</div>
                        <div className="inline-box label">Engine No</div>
                        <div className="inline-box value">{data.EngineNumber || '--'}</div>
                        <div className="inline-box label">Vehicle Age</div>
                        <div className="inline-box value">{data.vehicleAge || '--'}</div>
                    </div>
                    <div className="info-row last-row">
                        <div className="inline-box label">Fuel Type</div>
                        <div className="inline-box value">{data.fuelType || 'Petrol'}</div>
                        <div className="inline-box label">Vehicle Category</div>
                        <div className="inline-box value">{data.vehicleCategory || 'Private Car'}</div>
                        <div className="inline-box label child-A"></div>
                        <div className="inline-box value child-B"></div>
                    </div>
                </div>
            </div>

            <div className="section">
                <h4 className="section-heading">DRIVER PARTICULARS</h4>
                <div className="inline-box-row info-box">
                    <div className="info-row">
                        <div className="inline-box label">Driver Name</div>
                        <div className="inline-box value">{data.NameofDriver || 'Not Reported'}</div>
                        <div className="inline-box label">Driver License No</div>
                        <div className="inline-box value">{data.DrivingLicenseNo || data.driverLicenseNo || 'Not Reported'}</div>
                        <div className="inline-box label">Driver Age</div>
                        <div className="inline-box value">{data.driverAge || 'Not Reported'}</div>
                    </div>
                    <div className="info-row last-row">
                        <div className="inline-box label">Driver Experience</div>
                        <div className="inline-box value">{data.driverExperience || 'Not Reported'}</div>
                        <div className="inline-box label">Driver Valid License</div>
                        <div className="inline-box value">{data.DrivingLicenseNo || 'NA'}</div>
                        <div className="inline-box label child-A"></div>
                        <div className="inline-box value child-B"></div>
                    </div>
                </div>
            </div>

            <div className="section">
                <h4 className="section-heading">ACCIDENT & SURVEY PARTICULARS</h4>
                <div className="inline-box-row info-box">
                    <div className="info-row">
                        <div className="inline-box label">Date of Accident</div>
                        <div className="inline-box value">{data.dateOfAccident || '--'}</div>
                        <div className="inline-box label">Time of Accident</div>
                        <div className="inline-box value">{data.timeOfAccident || '--'}</div>
                        <div className="inline-box label">Place of Accident</div>
                        <div className="inline-box value">{data.placeOfAccident || '--'}</div>
                    </div>
                    <div className="info-row">
                        <div className="inline-box label">Survey Date</div>
                        <div className="inline-box value">{data.dateTimeOfSurvey || '--'}</div>
                        <div className="inline-box label">Survey Location</div>
                        <div className="inline-box value">{data.placeOfSurvey || '--'}</div>
                        <div className="inline-box label">Surveyor Name</div>
                        <div className="inline-box value">{data.surveyor || 'NA'}</div>
                    </div>
                </div>
            </div>

            <div>
                <h4 className="section-heading">Cause & Nature of Accident</h4>
                <p>
                    {data.causeAndNature || formData.causeAndNature || `As filled in the claim form, on the day and time of the accident, while proceeding on the way, near the place of the accident, water was logged.<br />
                    When the insured vehicle was passing through it, the vehicle stopped. The IV was then taken to the Hyundai dealer in Indore for a claim. After the repair work, while proceeding on the way, the IV stopped again. <br />
                    Matter was discussed with the ASM Hyundai Gwalior body shop associate. According to him, this incident happened on the Agra-Mathura Highway on 28.08.2023 at 9:30 PM. Since the previous loss was reported at the Indore dealer, the insured claimed the loss based on the previous claim and filled the claim details as per the previous claim.`}
                </p>
            </div>

            <div className="section">
                <table className="parts-table pdf-table-compact">
                    <thead>
                        <tr>
                            <th>Police Action</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{data.policeAction || 'Not reported as per the claim form'}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="section">
                <table className="parts-table pdf-table-compact">
                    <thead>
                        <tr>
                            <th>Details of Load/Passenger</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{data.loadPassengerDetails || 'Not Reported'}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="section">
                <table className="parts-table pdf-table-compact">
                    <thead>
                        <tr>
                            <th>Third Party Loss/Injuries</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{data.thirdPartyLoss || 'Nil as per the claim form.'}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div>
                <h4 className="section-heading">Particulars of Loss/Damages</h4>
                <p>
                    {data.particularsOfLoss || formData.particularsOfLoss ||  `In accordance with the instructions received from The New India Assurance Co. Ltd. D.O.-1, Gwalior, dated 20/09/2023, I visited A.S. Motors Pvt. Ltd., Gwalior and inspected the subject vehicle. The vehicle was reported to have met with an accident on 20/07/2023, and images of the vehicle were taken from different angles before dismantling.
                    During my final survey and physical inspection, I found a hole in the engine block at the lower backside. There was no external accidental impact. After telephonic discussion with the insured, he mentioned that while on the way, a sudden sound and smoke were observed, and the IV stopped. In my opinion, this loss might have occurred due to mechanical breakdown, which is excluded under the policy's own damage conditions. Hence, the assessed insurer liability is NIL.
                    The loss was discussed with the repairer and finally settled under policy terms, conditions, and approval of the insurers.`}
                    {/* <ul><li>
                        {data.additionalNote || 'The insured submitted an estimate claiming new parts.'}
                    </li></ul> */}
                </p>
            </div>

            {/* Testing Information */}
            <div className="section">
                <div className="note-section-pink">
                    <p>
                        For any further clarifications regarding your claim: Contact {data.contactEmail || 'Thiya.urmil@insurancecompany.com'} or {data.contactPhone || '7058676684'}. Alternatively, call the call center toll-free number 1800-258-5956.
                    </p>
                </div>
            </div>

            {/* Parts Table */}
            <div className="section">
                <table className="styled-table pdf-table-compact pdf-table-wrap">
                    <thead>
                        <tr>
                            <th>E.No</th>
                            <th>Parts Description</th>
                            <th>HSN</th>
                            <th>Bill S. No</th>
                            <th>Remark</th>
                            <th>Estimated</th>
                            <th colSpan="3">Assessed</th>
                            <th>GST</th>
                        </tr>
                        <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th>Glass 2nd HandRepair</th>
                            <th>Metal (10)</th>
                            <th>Non Metal</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                       {data.parts && data.parts.map((part, index) => {
        // Material के आधार पर Metal/Non Metal निर्धारित करें
        const materialType = part.material || '';
        const isMetal = materialType.toLowerCase().includes('metal');
        const isPlastic = materialType.toLowerCase().includes('plastic') || 
                         materialType.toLowerCase().includes('non-metal') ||
                         materialType.toLowerCase().includes('non metal');

        return (
            <tr key={index}>
                <td>{index + 1}</td>
                <td className="pdf-table-wrap">{part.description || 'Screw-Tapping'}</td>
                <td>{part.hsn || '87089900'}</td>
                <td>{part.billSerialNo || '35'}</td>
                <td>{part.remark || 'Safe'}</td>
                <td>₹ {part.totalAmount || '725.70'}</td>
                <td>{part.glassRepair || '-'}</td>
                {/* Metal/Non Metal mapping */}
                <td>{isMetal ? 'Metal' : '-'}</td>
                <td>{isPlastic ? 'Plastic' : (isMetal ? '-' : 'Plastic')}</td>
                <td>{part.gst || '0.0'}</td>
            </tr>
        );
    })}
    
    {/* Fallback if no parts data */}
    {(!data.parts || data.parts.length === 0) && (
        <>
            <tr>
                <td>1</td>
                <td className="pdf-table-wrap">Screw-Tapping</td>
                <td>87089900</td>
                <td>35</td>
                <td>Safe</td>
                <td>₹ 725.70</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>0.0</td>
            </tr>
            <tr>
                <td>2</td>
                <td className="pdf-table-wrap">Screw-Flange Head</td>
                <td>87089900</td>
                <td>35</td>
                <td>Safe</td>
                <td>₹ 725.70</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>0.0</td>
            </tr>
        </>
    )}

 {/* CALCULATION LOGIC START */}
            {/* CALCULATION LOGIC - ALL PARTS ESTIMATED AMOUNTS */}
{(() => {
    let totalEstimated = 0;
    let totalGST = 0;
    
    console.log('=== PSU REPORT DEBUG ===');
    console.log('Total parts:', data.parts ? data.parts.length : 0);
    
    if (data.parts && data.parts.length > 0) {
        data.parts.forEach((part, index) => {
            // Different possible field names for estimated amount
            const estimated = 
                parseFloat(part.totalAmount) || 
                parseFloat(part.estimated) ||
                parseFloat(part.amount) ||
                parseFloat(part.totalAmount) ||
                parseFloat(part.rate) || 
                0;
                
            const gst = parseFloat(part.gst) || 0;
            
            totalEstimated += estimated;
            totalGST += gst;
            
            // Debug log for each part
            console.log(`Part ${index + 1}:`, {
                description: part.description,
                estimatedAmount: part.estimatedAmount,
                estimated: part.estimated,
                amount: part.amount,
                totalAmount: part.totalAmount,
                rate: part.rate,
                calculatedEstimated: estimated,
                runningTotal: totalEstimated
            });
        });
    }
    
    const grandTotal = totalEstimated + totalGST;
    
    console.log('=== FINAL TOTALS ===');
    console.log('Total Estimated:', totalEstimated);
    console.log('Total GST:', totalGST);
    console.log('Grand Total:', grandTotal);
    console.log('====================');
    
    return ( 
        <>
            {/* GST Row */}
            <tr>
                <td colSpan="5">Add Applicable GST</td>
                <td>₹ {totalGST.toFixed(2)}</td>
                <td>-</td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            
            {/* Total Before Depreciation */}
            <tr>
                <td colSpan="5">Total</td>
                <td>₹ {totalEstimated.toFixed(2)}</td>
                <td>-</td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            
            {/* Depreciation Row */}
            <tr>
                <td colSpan="5">Less : Depreciation</td>
                <td>0.00</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            
            {/* Net Total */}
            <tr>
                <td colSpan="5">Net Total ₹</td>
                <td>₹ {totalEstimated.toFixed(2)}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            
            {/* Grand Total - ALL PARTS TOTAL */}
            <tr>
                <td colSpan="5"><strong>Grand Total</strong></td>
                <td><strong>₹ {grandTotal.toFixed(2)}</strong></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
        </>
    );
})()}
                    </tbody>
                </table>
            </div>

            {/* Labour and Repairs */}
            <div className="section">
                <h4 className="section-heading">LABOUR AND REPAIRS</h4>
            </div>

            <div className="section">
                <table className="styled-table pdf-table-compact pdf-table-wrap">
                    <thead>
                        <tr>
                            <th>E.No</th>
                            <th>SAC</th>
                            <th>Labour Description</th>
                            <th>Estimated ₹</th>
                            <th>Assessed ₹</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.labourDetails && data.labourDetails.map((labour, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{labour.sac || '87089900'}</td>
                                <td className="pdf-table-wrap">{labour.labourDescription || 'Screw-Tapping'}</td>
                                <td>₹ {labour.totalAmount || '725.70'}</td>
                                <td>₹ {labour.assessedAmount || '-'}</td>
                            </tr>
                        ))}
                        {/* Fallback if no labour data */}
                        {(!data.labourDetails || data.labourDetails.length === 0) && (
                            <>
                                <tr>
                                    <td>1</td>
                                    <td>87089900</td>
                                    <td className="pdf-table-wrap">Screw-Tapping</td>
                                    <td>₹ 725.70</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>2</td>
                                    <td>87089900</td>
                                    <td className="pdf-table-wrap">Screw-Flange Head</td>
                                    <td>₹ 725.70</td>
                                    <td>-</td>
                                </tr>
                            </>
                        )}
                        <tr>
                            <td colSpan="3"></td>
                            <td>₹ 725.70</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td colSpan="3"></td>
                            <td>Add Applicable GST</td>
                            <td>0.0</td>
                        </tr>
                        <tr>
                            <td colSpan="3"></td>
                            <td>Total</td>
                            <td>₹ 725.70</td>
                        </tr>
                        <tr>
                            <td colSpan="3"></td>
                            <td>Less : Depreciation</td>
                            <td>0.0</td>
                        </tr>
                        <tr>
                            <td colSpan="3"></td>
                            <td>Net Total ₹</td>
                            <td>₹ 725.70</td>
                        </tr>
                        <tr>
                            <td colSpan="3"></td>
                            <td>Grand Total</td>
                            <td>₹ 725.70</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Summary of Assessment */}
            <div className="custom-table-container section">
                <table className="custom-table styled-tabled pdf-table-compact">
                    <thead className="custom-table-header">
                        <tr>
                            <th>Particulars</th>
                            <th>Estimated ₹</th>
                            <th>Assessed ₹</th>
                        </tr>
                    </thead>
                    <tbody className="custom-table-body">
                        <tr>
                            <td>Screw-Tapping</td>
                            <td>₹ 725.70</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td>Screw-Flange Head</td>
                            <td>₹ 725.70</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>₹ 725.70</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Add Applicable GST</td>
                            <td>0.0</td>
                        </tr>
                        <tr>
                            <td></td>
                            <td><strong>Total</strong></td>
                            <td><strong>₹ 725.70</strong></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>Less : Depreciation</td>
                            <td>0.0</td>
                        </tr>
                        <tr>
                            <td></td>
                            <td><strong>Net Total ₹</strong></td>
                            <td><strong>₹ 725.70</strong></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td><strong>Grand Total</strong></td>
                            <td><strong>₹ 725.70</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h4 className="section-heading">Depreciation (0.00) is not deducted being NIL DEPRECIATION policy.</h4>

            {/* GST Summary */}
            <div className="tax-summary-container-review">
                <h4 className="section-heading-review">GST SUMMARY TAX WISE</h4>
                <table className="tax-summary-table-review pdf-table-compact">
                    <thead>
                        <tr>
                            <th>SN NO.</th>
                            <th>Tax %</th>
                            <th>Actual</th>
                            <th>C-OST</th>
                            <th>S-OST</th>
                            <th>I-OST</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                        </tr>
                        <tr className="grand-total-row-review">
                            <td colSpan="2">Grand Total</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                            <td>0.00</td>
                        </tr>
                        <tr className="total-tax-row-review">
                            <td colSpan="2">Total Tax</td>
                            <td></td>
                            <td>0.00</td>
                            <td></td>
                            <td>0.00</td>
                            <td>0.00</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="tax-summary-container-last">
                <table className="tax-summary-table-last pdf-table-compact">
                    <thead>
                        <tr>
                            <th>SN NO.</th>
                            <th>Service Acc. Code</th>
                            <th>GST %</th>
                            <th>Actual Allowed</th>
                            <th>C GST</th>
                            <th>S GST</th>
                            <th>I GST</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>9987</td>
                            <td>18%</td>
                            <td>1000.00</td>
                            <td>90.00</td>
                            <td>90.00</td>
                            <td>180.00</td>
                            <td>1360.00</td>
                        </tr>
                        <tr className="grand-total-row-last">
                            <td colSpan="3">Grand Total</td>
                            <td>1000.00</td>
                            <td>90.00</td>
                            <td>90.00</td>
                            <td>180.00</td>
                            <td>1360.00</td>
                        </tr>
                        <tr className="total-tax-row-last">
                            <td colSpan="3">Total Tax</td>
                            <td></td>
                            <td>90.00</td>
                            <td>90.00</td>
                            <td>180.00</td>
                            <td>360.00</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Final Calculation */}
            <h4 className="section-heading-last">FINAL CALCULATION</h4>
            <table className="final-table-last pdf-table-compact">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Total Liability</td>
                        <td>{data?.finalCalculation?.totalLiability && data.finalCalculation.totalLiability !== 'Not Found' ? `₹${data.finalCalculation.totalLiability}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>Compulsory Deductible</td>
                        <td>{data?.finalCalculation?.compulsoryDeductible && data.finalCalculation.compulsoryDeductible !== 'Not Found' ? `₹${data.finalCalculation.compulsoryDeductible}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>Voluntary Deductible</td>
                        <td>{data?.finalCalculation?.voluntaryDeductible && data.finalCalculation.voluntaryDeductible !== 'Not Found' ? `₹${data.finalCalculation.voluntaryDeductible}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>Salvage Deductions</td>
                        <td>{data?.finalCalculation?.salvageDeductions && data.finalCalculation.salvageDeductions !== 'Not Found' ? `₹${data.finalCalculation.salvageDeductions}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>Non-Standard Amount</td>
                        <td>{data?.finalCalculation?.nonStandardAmount && data.finalCalculation.nonStandardAmount !== 'Not Found' ? `₹${data.finalCalculation.nonStandardAmount}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>Towing Amount</td>
                        <td>{data?.finalCalculation?.towingAmount && data.finalCalculation.towingAmount !== 'Not Found' ? `₹${data.finalCalculation.towingAmount}` : '--'}</td>
                    </tr>
                    <tr>
                        <td>FINAL AMOUNT</td>
                        <td>{data?.finalCalculation?.digitLiability && data.finalCalculation.digitLiability !== 'Not Found' ? `₹${data.finalCalculation.digitLiability}` : '--'}</td>
                    </tr>
                </tbody>
            </table>

            {/* Assessment Notes */}
            <div className="section">
                <h4 className="section-heading">ASSESSMENT NOTES</h4>
                <div className="note-section assessment-notes">
                    <ul>
                        <li>Any additional amount over and above the approved assessment shall be borne by the Insured.</li>
                        <li>The final liability has been arrived at as full and final settlement after consent from the Insured. Do get back to us within the next 24 hours for any queries or clarifications.</li>
                    </ul>
                </div>
            </div>

            {/* Footer Information */}
            <div className="note-section">
                <h4 className="section-heading">NOTES</h4>
                <p>
                    {data.finalNotes || `As per my observations in the loss reported at ASM Hyundai Gwalior, no insured peril play/operated at the time of loss & loss seems due to some mechanical breakdown, which is one exclusion in own damage policy conditions. Hence undersigned
                    assessed insurer liability NIL.`}
                </p>
                <p>
                    Thanking you and assuring you of my best services at all times,<br />
                    Issued Without Prejudice
                </p>
                <p>
                    ({data.insured})<br />
                    Surveyor & Loss Assessor
                </p>
                <p>
                    Enclosures: Estimate of repairs, Intimation Form, Claim Form, 64 VB, Photographs [12 Nos.], Survey Fee Bill, Photocopy of Policy DL, RC, Insured Aadhaar, PAN Card & E-Sewa Copy of D.L. & R.C.
                </p>
            </div>
        </div>
    );
};

export default PSUReport;